import { Group, Button, useMantineTheme } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { memo, useState } from "react";
import {
  CreateLocationTaxRateInput,
  GetLocationTaxRatesQuery,
  GetOrganizationTaxRatesQuery,
  useCreateLocationTaxRateMutation,
  useGetOrganizationTaxRatesQuery,
  useUpdateLocationTaxRateMutation,
} from "src/generated/graphql";
import { number, object, string } from "yup";
import { closeAllModals } from "@mantine/modals";
import { ITaxRateElement } from ".";
import Select from "src/components/Select";
import { useAppContext } from "src/api/AppContext";
import NumberInput from "src/components/NumberInput/NumberInput";

interface IFormProps
  extends Omit<CreateLocationTaxRateInput, "organizationTaxRate"> {
  organizationTaxRate?: string;
}

export const LocationTaxRateFormSchema = object().shape({
  organizationTaxRate: string().required("Tax Type is required"),
  taxRate: number().required(),
});

interface IProps {
  formData?: ITaxRateElement;
  locationId: string;
  refetch: () => void;
  locTaxes: GetLocationTaxRatesQuery["locationTaxRateMany"];
}

const TaxRateDetail = ({ formData, locationId, refetch, locTaxes }: IProps) => {
  const theme = useMantineTheme();
  const { currentOrganization } = useAppContext();
  const [orgtaxRates, setorgtaxRates] = useState<
    GetOrganizationTaxRatesQuery["organizationTaxRateMany"]
  >([]);
  const form = useForm<IFormProps>({
    initialValues: {
      taxRate: formData?.taxRate || 0,
      organizationTaxRate: formData?.organizationTaxRate?.id || "",
      location: { id: locationId },
    },
    validate: yupResolver(LocationTaxRateFormSchema),
  });

  useGetOrganizationTaxRatesQuery({
    variables: { organizationId: currentOrganization?.id || "" },
    skip: !currentOrganization?.id,
    onCompleted: (data) => {
      // filter out already added organizationTaxRateIds based on location
      const filteredData =
        data.organizationTaxRateMany?.filter(
          (orgTaxes) =>
            !locTaxes.find((e) => e.organizationTaxRate.id === orgTaxes.id)
        ) || [];
      setorgtaxRates(filteredData);
    },
  });

  const [createTaxRate, { loading: createLoading }] =
    useCreateLocationTaxRateMutation({
      onCompleted() {
        refetch();
        closeAllModals();
      },
    });

  const [updateTaxRate, { loading: editLoading }] =
    useUpdateLocationTaxRateMutation({
      onCompleted() {
        refetch();
        closeAllModals();
      },
    });

  const handleUpdate = async (values: IFormProps) => {
    const { organizationTaxRate, ...rest } = values;
    if (!organizationTaxRate) return;
    const obj = {
      ...rest,
      organizationTaxRate: { id: organizationTaxRate },
    };
    formData?.id
      ? updateTaxRate({
          variables: {
            data: obj,
            id: formData.id,
          },
        })
      : createTaxRate({
          variables: {
            data: obj,
          },
        });
  };

  const handleOrgTaxUpdate = (val: string) => {
    const taxRate = orgtaxRates?.find((o) => o.id === val)?.taxRate || 0;
    form.setFieldValue("organizationTaxRate", val);
    form.setFieldValue("taxRate", taxRate);
  };

  return (
    <form onSubmit={form.onSubmit((values) => handleUpdate(values))}>
      <Select
        data={
          orgtaxRates?.map((val) => ({
            value: val?.id,
            label: val.name,
            tax: val.taxRate,
          })) || []
        }
        label="Tax Type"
        {...form.getInputProps("organizationTaxRate")}
        onChange={(e: string) => handleOrgTaxUpdate(e)}
      />

      <NumberInput
        max={100}
        label="Tax Rate"
        parser={(value) => value?.replace(/[%]/g, "")}
        formatter={(value) =>
          !Number.isNaN(parseFloat(value as string)) ? `${value}%` : ""
        }
        precision={4}
        {...form.getInputProps("taxRate")}
      />

      <Group position="apart" mt="md" style={{ marginTop: 30 }}>
        <Button
          radius="md"
          variant="outline"
          onClick={() => closeAllModals()}
          style={{
            flex: 1,
            color: theme.colors.secondaryTextColor[6],
          }}
          sx={{
            backgroundColor: theme.colors.secondary[6],
            "&:hover": {
              background: theme.colors.secondary[5],
            },
          }}
        >
          Cancel
        </Button>
        <Button
          radius="md"
          type="submit"
          style={{
            flex: 1,
          }}
          sx={{
            color: theme.colors.primaryTextColor[6],
            "&:hover": {
              backgroundColor: theme.colors.primary[4],
            },
          }}
          loading={createLoading || editLoading}
        >
          {formData?.id ? "Save" : "Create"}
        </Button>
      </Group>
    </form>
  );
};

export default memo(TaxRateDetail);
